import * as React from 'react';
import { numberToPrettyString, mergeStyles } from '@cian/utils';

import { Text } from '../Text';
import * as styles from './SimpleInput.css';
import { getEventValue } from '@cian/newbuilding-utils';

export type TChildPosition = 'first-child' | 'last-child';

export interface ISimpleInputProps extends React.HTMLProps<HTMLInputElement> {
  insideLabel?: string;
  childPosition?: TChildPosition;
  onChangeValue?(value: string): void;
}

interface ISimpleInputState {
  value: string;
  caretPosition: number | null;
}
const MAX_NUMBER_LENGTH = 13;

export class SimpleInput extends React.Component<ISimpleInputProps, ISimpleInputState> {
  public state: ISimpleInputState = {
    value: String(this.props.value || ''),
    caretPosition: null,
  };
  private inputRef = React.createRef<HTMLInputElement>();

  public static getDerivedStateFromProps(nextProps: ISimpleInputProps, state: ISimpleInputState) {
    if (nextProps.value === state.value) {
      return null;
    }

    return { value: String(nextProps.value || '') };
  }

  public shouldComponentUpdate(nextProps: ISimpleInputProps, nextState: ISimpleInputState) {
    return nextState.value !== this.state.value
      || nextProps.insideLabel !== this.props.insideLabel
      || nextProps.childPosition !== this.props.childPosition;
  }

  public componentDidUpdate() {
    if (this.inputRef.current) {
      const newCaretPosition = this.calculateNewCaretPositon(this.inputRef.current.value, this.state.caretPosition);

      this.inputRef.current.selectionStart = newCaretPosition;
      this.inputRef.current.selectionEnd = newCaretPosition;
    }
  }

  public render() {
    const {
      insideLabel,
      childPosition,
    } = this.props;

    return (
      <div
        {...mergeStyles([
          styles['field'],
          insideLabel && styles['has_inside_label'],
        ])}
        data-mark="SimpleInput"
      >
        <input
          ref={this.inputRef}
          {...mergeStyles([
            styles['input'],
            childPosition && styles[childPosition],
          ])}
          {...this.getInputProps()}
        />
        {insideLabel && (
          <div className={styles['inside_label']}>
            <Text
              fontSizeMobile={16}
              fontSizeDesktop={14}
            >
              {insideLabel}
            </Text>
          </div>
        )}
      </div>
    );
  }

  private handlerOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      onChange,
      onChangeValue,
    } = this.props;
    const { inputRef: { current } } = this;
    const eventValue: string = getEventValue(e);
    let value = eventValue.replace(/ /g, '').replace(',', '.');
    const caretPosition = this.calculateNewCaretPositon(eventValue, e.target.selectionStart);

    if (value === this.state.value && current && current.selectionStart) {
      const formattedValue = eventValue.slice(0, current.selectionStart - 1) + eventValue.slice(current.selectionStart);
      value = formattedValue.replace(/ /g, '').replace(',', '.');
    }

    if (this.isValidStringFloat(value)) {
      this.setState({ value, caretPosition }, () => {
        if (onChangeValue) {
          onChangeValue(value);
        }
      });
    }
    if (onChange) {
      onChange(e);
    }
  }

  private isValidStringFloat = (value: string) => /(^\d*\.\d{0,2}$)|(^\d*$)/.test(value);

  private getInputProps = () => {
    const { type, insideLabel, ...other } = this.props;

    if (type === 'number') {
      const {
        value,
      } = this.state;
      const [
        prefix,
        suffix = '',
      ] = value.split('.');
      const formatValue = `${
        numberToPrettyString(parseInt(prefix.substr(0, MAX_NUMBER_LENGTH), 10))
      }${
        /\.\d*$/.test(value) ? ',' : ''
      }${
        suffix
      }`;

      return {
        ...other,
        type: 'text',
        value: formatValue,
        onChange: this.handlerOnChange,
      };
    }

    return {
      type,
      ...other,
    };
  }

  private calculateNewCaretPositon = (value: string, caretPosition: number | null) => {
    return value.length - Number(caretPosition);
  }
}
